import type { SellerPermission } from "@/lib/api/types";

// Localized labels for the RBAC permission catalog and the system roles.
// Kept as a co-located reference table (consistent with the existing
// code-generated labels) rather than ~150 i18n-JSON entries; the i18n commit
// gate only flags JSX text nodes, and these are consumed as expressions.

type Bi = { ar: string; en: string };

function pick(bi: Bi | undefined, locale: string, fallback: string): string {
  if (!bi) return fallback;
  return locale === "ar" ? bi.ar : bi.en;
}

// Permission group = the first segment of the enum (PRODUCT_VIEW → PRODUCT).
export function permissionGroupOf(permission: SellerPermission): string {
  return permission.split("_")[0] ?? "OTHER";
}

const GROUP_LABELS: Record<string, Bi> = {
  STORE: { ar: "المتجر", en: "Store" },
  PRODUCT: { ar: "المنتجات", en: "Products" },
  CATEGORY: { ar: "التصنيفات", en: "Categories" },
  ORDER: { ar: "الطلبات", en: "Orders" },
  COUPON: { ar: "الكوبونات", en: "Coupons" },
  REVIEW: { ar: "التقييمات", en: "Reviews" },
  SHIPPING: { ar: "الشحن", en: "Shipping" },
  PAYMENT: { ar: "الدفع", en: "Payment" },
  MEMBER: { ar: "الفريق", en: "Team" },
  ROLE: { ar: "الصلاحيات", en: "Roles" },
};

const PERMISSION_LABELS: Record<string, Bi> = {
  STORE_VIEW: { ar: "عرض المتجر", en: "View store" },
  STORE_EDIT: { ar: "تعديل المتجر", en: "Edit store" },
  PRODUCT_VIEW: { ar: "عرض المنتجات", en: "View products" },
  PRODUCT_CREATE: { ar: "إضافة منتج", en: "Create products" },
  PRODUCT_EDIT: { ar: "تعديل المنتجات", en: "Edit products" },
  PRODUCT_DELETE: { ar: "حذف المنتجات", en: "Delete products" },
  CATEGORY_VIEW: { ar: "عرض التصنيفات", en: "View categories" },
  CATEGORY_CREATE: { ar: "إضافة تصنيف", en: "Create categories" },
  CATEGORY_EDIT: { ar: "تعديل التصنيفات", en: "Edit categories" },
  CATEGORY_DELETE: { ar: "حذف التصنيفات", en: "Delete categories" },
  ORDER_VIEW: { ar: "عرض الطلبات", en: "View orders" },
  ORDER_EDIT: { ar: "تعديل الطلبات", en: "Edit orders" },
  ORDER_FULFILL: { ar: "تجهيز الطلبات", en: "Fulfill orders" },
  COUPON_VIEW: { ar: "عرض الكوبونات", en: "View coupons" },
  COUPON_CREATE: { ar: "إضافة كوبون", en: "Create coupons" },
  COUPON_EDIT: { ar: "تعديل الكوبونات", en: "Edit coupons" },
  COUPON_DELETE: { ar: "حذف الكوبونات", en: "Delete coupons" },
  REVIEW_VIEW: { ar: "عرض التقييمات", en: "View reviews" },
  REVIEW_MODERATE: { ar: "إدارة التقييمات", en: "Moderate reviews" },
  SHIPPING_VIEW: { ar: "عرض الشحن", en: "View shipping" },
  SHIPPING_EDIT: { ar: "تعديل الشحن", en: "Edit shipping" },
  PAYMENT_VIEW: { ar: "عرض الدفع", en: "View payment" },
  PAYMENT_EDIT: { ar: "تعديل الدفع", en: "Edit payment" },
  MEMBER_VIEW: { ar: "عرض الفريق", en: "View team" },
  MEMBER_INVITE: { ar: "دعوة أعضاء", en: "Invite members" },
  MEMBER_REMOVE: { ar: "إزالة أعضاء", en: "Remove members" },
  MEMBER_EDIT_ROLES: { ar: "تعديل صلاحيات الأعضاء", en: "Edit member roles" },
  ROLE_VIEW: { ar: "عرض الصلاحيات", en: "View roles" },
  ROLE_CREATE: { ar: "إنشاء صلاحية", en: "Create roles" },
  ROLE_EDIT: { ar: "تعديل الصلاحيات", en: "Edit roles" },
  ROLE_DELETE: { ar: "حذف الصلاحيات", en: "Delete roles" },
};

const SYSTEM_ROLE_NAME: Record<string, Bi> = {
  OWNER: { ar: "المالك", en: "Owner" },
  MANAGER: { ar: "مدير", en: "Manager" },
  FULFILLMENT: { ar: "التجهيز", en: "Fulfillment" },
  SUPPORT: { ar: "الدعم", en: "Support" },
};

const SYSTEM_ROLE_DESC: Record<string, Bi> = {
  OWNER: { ar: "صلاحية كاملة على المتجر", en: "Full access to the store" },
  MANAGER: {
    ar: "إدارة المنتجات والطلبات والكوبونات والشحن",
    en: "Manages products, orders, coupons and shipping",
  },
  FULFILLMENT: {
    ar: "عرض وتجهيز الطلبات",
    en: "Views and fulfills orders",
  },
  SUPPORT: {
    ar: "وصول للقراءة فقط لدعم العملاء",
    en: "Read-only access for customer support",
  },
};

// Fallback: title-case the raw enum (e.g. "Product View") for unknown values.
function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function permissionLabel(
  permission: SellerPermission,
  locale: string,
): string {
  return pick(PERMISSION_LABELS[permission], locale, titleCase(permission));
}

export function permissionGroupLabel(group: string, locale: string): string {
  return pick(GROUP_LABELS[group], locale, titleCase(group));
}

// For a system role, prefer the localized catalog label; custom roles fall
// back to the seller-entered name/description.
export function systemRoleName(
  key: string,
  locale: string,
  fallback: string,
): string {
  return pick(SYSTEM_ROLE_NAME[key], locale, fallback);
}

export function systemRoleDesc(
  key: string,
  locale: string,
  fallback: string | null,
): string | null {
  const bi = SYSTEM_ROLE_DESC[key];
  if (!bi) return fallback;
  return locale === "ar" ? bi.ar : bi.en;
}

// Access-tier labels + one-line helpers, in warm plain Arabic (not literal
// "Viewer/Editor"). Keyed by the tier keys from permission-tiers.ts.
const TIER_LABEL: Record<string, Bi> = {
  none: { ar: "مقفول", en: "Locked" },
  view: { ar: "يتفرّج", en: "View only" },
  manage: { ar: "يساعد", en: "Can manage" },
  full: { ar: "كامل", en: "Full access" },
  custom: { ar: "مخصص", en: "Custom" },
};

const TIER_HELPER: Record<string, Bi> = {
  none: {
    ar: "مش هيقدر يفتح القسم ده.",
    en: "No access to this section.",
  },
  view: {
    ar: "يشوف بس — مش هيعدّل أو يحذف حاجة.",
    en: "Can view only — no editing or deleting.",
  },
  manage: {
    ar: "يضيف ويعدّل — من غير حذف.",
    en: "Can add and edit — but not delete.",
  },
  full: {
    ar: "يعمل كل حاجة في القسم، بما فيها الحذف.",
    en: "Can do everything here, including delete.",
  },
  custom: {
    ar: "أذونات مختارة يدويًا.",
    en: "Hand-picked permissions.",
  },
};

export function tierLabel(tier: string, locale: string): string {
  return pick(TIER_LABEL[tier], locale, tier);
}

export function tierHelper(tier: string, locale: string): string {
  return pick(TIER_HELPER[tier], locale, "");
}

// Small UI strings for the access-tier editor (kept here with the other RBAC
// text so they render as expressions, gate-safe).
const UI_TEXT: Record<string, Bi> = {
  showPermissions: { ar: "عرض الأذونات", en: "Show permissions" },
  hidePermissions: { ar: "إخفاء الأذونات", en: "Hide permissions" },
  startFrom: { ar: "ابدأ من قالب", en: "Start from a template" },
  fromScratch: { ar: "من الصفر", en: "From scratch" },
  leastPrivilegeHint: {
    ar: "كل الأقسام مقفولة. افتح بس اللي محتاجه الموظف — ده الأأمن.",
    en: "Everything is locked. Open only what this member needs — that's safest.",
  },
  rolesAreaWarning: {
    ar: "تنبيه: لو فتحت «الصلاحيات» للموظف، هيقدر يدّي وصول لأي حد — حتى نفسه.",
    en: "Heads up: granting Roles access lets this member give anyone access — including themselves.",
  },
};

export function uiText(key: string, locale: string): string {
  return pick(UI_TEXT[key], locale, key);
}
